// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { horizontalBars } from '../components/charts';
import { bySector, rankedBy } from '../analysis';
import { positionColour, OVER, UNDER, NEUTRAL, sectorColour } from '../sectors';
import { escapeHtml, formatCompact, formatNumber, formatPercent } from '../utils/format';
import { glossaryTerm } from '../glossary';

const mt = (t: number) => `${(t / 1e6).toFixed(1)} Mt`;

export function renderOverview(root: HTMLElement, ctx: ViewContext): void {
  const { facilities, meta } = ctx.data;
  const n = meta.national;
  const overPct = (n.nOver / n.facilities) * 100;

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Australia's biggest carbon emitters, against the line</h1>
      <p class="view-sub">
        The ${escapeHtml(meta.reportingYear)} ${glossaryTerm('safeguard', 'Safeguard Mechanism')} covers
        ${formatNumber(n.facilities)} industrial facilities — every operation that releases more than 100,000 tonnes of
        ${glossaryTerm('co2e', 'CO₂-equivalent')} a year. Each has a ${glossaryTerm('baseline', 'baseline')} that falls
        toward net zero. The question this site answers: who came in over it?
      </p>
    </div>

    <div class="hero">
      <div class="panel">
        <div class="big-ratio">${n.nOver} of ${n.facilities}</div>
        <p class="hero-lead" style="margin-top:var(--space-sm)">
          of Australia's largest emitting facilities emitted <strong>above their own baseline</strong> this year —
          ${overPct.toFixed(0)}% of them — even as the cap tightens toward net zero by 2050. Together they released
          <strong>${mt(n.totalCovered)}</strong> of ${glossaryTerm('covered', 'covered emissions')} against an aggregate
          baseline of <strong>${mt(n.totalBaseline)}</strong> — ${mt(n.totalExcess)} over the line.
        </p>
        <div class="mix-bar tall" data-role="split" style="margin-top:var(--space-lg)"></div>
        <div class="legend" style="margin-top:var(--space-sm)">
          <span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${OVER}"></span>Over baseline (${n.nOver})</span>
          <span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${UNDER}"></span>Under baseline (${n.nUnder})</span>
          <span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${NEUTRAL}"></span>At baseline (${n.nAt})</span>
        </div>
      </div>
      <div class="panel">
        <div class="panel-title">The gases behind the number</div>
        <div class="panel-sub">${mt(n.co2)} carbon dioxide, plus ${mt(n.ch4)} of ${glossaryTerm('methane', 'fugitive methane')} — ${formatPercent(n.methaneShare)} of the total, tonne-for-tonne the most damaging part.</div>
        <div class="mix-bar tall" data-role="gasbar"></div>
        <div class="legend" style="margin-top:var(--space-sm)">
          <span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:#6b7280"></span>Carbon dioxide ${formatPercent((n.co2 / n.totalCovered) * 100)}</span>
          <span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${OVER}"></span>Methane ${formatPercent(n.methaneShare)}</span>
          <span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:#8b5cf6"></span>Nitrous oxide ${formatPercent((n.n2o / n.totalCovered) * 100)}</span>
        </div>
      </div>
    </div>

    <div class="stat-grid">
      <div class="stat"><div class="stat-label">Covered emissions</div><div class="stat-value">${mt(n.totalCovered)}</div><div class="stat-note">~a quarter of Australia's total</div></div>
      <div class="stat"><div class="stat-label">Over the line</div><div class="stat-value over">${formatNumber(n.nOver)}</div><div class="stat-note">facilities above baseline</div></div>
      <div class="stat"><div class="stat-label">Under the line</div><div class="stat-value under">${formatNumber(n.nUnder)}</div><div class="stat-note">earned ${formatCompact(n.smcsIssued)} credits</div></div>
      <div class="stat"><div class="stat-label">Credits surrendered</div><div class="stat-value slate">${formatCompact(n.unitsSurrendered)}</div><div class="stat-note">to cover excess emissions</div></div>
      <div class="stat"><div class="stat-label">Industries</div><div class="stat-value slate">${formatNumber(n.sectors)}</div><div class="stat-note">${formatNumber(n.companies)} responsible companies</div></div>
    </div>

    <div class="grid-2 wide">
      <div class="panel">
        <div class="panel-title">The ten largest emitting facilities</div>
        <div class="panel-sub">By covered emissions. Bar colour shows whether each came in over (ember) or under (teal) its baseline. Click any bar to open the facility.</div>
        <div class="chart-scroll" data-role="top"></div>
      </div>
      <div class="panel">
        <div class="panel-title">Where the emissions come from</div>
        <div class="panel-sub">Covered emissions by industry. Coal mining and gas dominate. Click to explore sectors.</div>
        <div class="chart-scroll" data-role="sectors"></div>
      </div>
    </div>
  `;

  // Over/under split bar (by facility count, area = emissions weighting).
  const overCov = facilities.filter((f) => f.excess > 0.5).reduce((s, f) => s + f.covered, 0);
  const underCov = facilities.filter((f) => f.excess < -0.5).reduce((s, f) => s + f.covered, 0);
  const atCov = n.totalCovered - overCov - underCov;
  const split = root.querySelector('[data-role="split"]') as HTMLElement;
  split.innerHTML = [
    { c: OVER, v: overCov, l: `Over baseline · ${mt(overCov)} from ${n.nOver} facilities` },
    { c: NEUTRAL, v: atCov, l: `At baseline · ${mt(atCov)}` },
    { c: UNDER, v: underCov, l: `Under baseline · ${mt(underCov)} from ${n.nUnder} facilities` },
  ]
    .filter((s) => s.v > 0)
    .map((s) => `<div class="mix-seg" style="flex:${s.v};background:${s.c}" data-tip="${escapeHtml(s.l)}"></div>`)
    .join('');

  const gasbar = root.querySelector('[data-role="gasbar"]') as HTMLElement;
  gasbar.innerHTML = [
    { c: '#6b7280', v: n.co2, l: `Carbon dioxide · ${mt(n.co2)}` },
    { c: OVER, v: n.ch4, l: `Methane · ${mt(n.ch4)}` },
    { c: '#8b5cf6', v: n.n2o, l: `Nitrous oxide · ${mt(n.n2o)}` },
    { c: '#cbd5e1', v: n.ghgOther, l: `Other · ${mt(n.ghgOther)}` },
  ]
    .filter((s) => s.v > 0)
    .map((s) => `<div class="mix-seg" style="flex:${s.v};background:${s.c}" data-tip="${escapeHtml(s.l)}"></div>`)
    .join('');

  // Top-10 facilities.
  const top = rankedBy(facilities, 'covered').slice(0, 10);
  (root.querySelector('[data-role="top"]') as HTMLElement).appendChild(
    horizontalBars(
      top.map((f) => ({
        id: f.slug,
        label: f.name.length > 26 ? f.name.slice(0, 25) + '…' : f.name,
        value: f.covered,
        colour: positionColour(f.excess),
        tip: `${f.name}\n${f.company}\n${formatCompact(f.covered)} tCO₂-e covered · baseline ${formatCompact(f.baseline)}\n${f.excess > 0 ? `+${formatCompact(f.excess)} over` : `${formatCompact(f.excess)} under`}`,
      })),
      { width: 620, labelWidth: 180, valueFormat: (v) => formatCompact(v), onClick: (id) => ctx.openFacility(id) },
    ),
  );

  // Sectors.
  const sectors = bySector(facilities).slice(0, 10);
  (root.querySelector('[data-role="sectors"]') as HTMLElement).appendChild(
    horizontalBars(
      sectors.map((s) => ({
        id: s.key,
        label: s.label.length > 24 ? s.label.slice(0, 23) + '…' : s.label,
        value: s.covered,
        colour: sectorColour(s.label),
        tip: `${s.label}\n${formatCompact(s.covered)} tCO₂-e · ${s.count} facilities\n${s.nOver} over baseline`,
      })),
      { width: 620, labelWidth: 180, valueFormat: (v) => formatCompact(v), onClick: () => ctx.goTo('sectors') },
    ),
  );
}
